import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";

import { X as CloseIcon, Paperclip, Trash2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import ConfirmationModal from "../ConfirmationModal";
import ChatiaDialogActions from "../ChatiaDialogActions";
import AppTooltip from "../AppTooltip";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";
  return {
    dialogPaper: {
      borderRadius: "21px 21px 12px 12px !important",
      maxHeight: "85vh !important",
      display: "flex !important",
      flexDirection: "column !important",
      overflow: "hidden !important",
      background: isDark ? "#121212" : "#ffffff",
      backgroundImage: "none !important",
      border: "none",
      "& form": {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      },
      "& .MuiDialogContent-root": {
        flex: 1,
        overflowY: "auto !important",
        overflowX: "hidden",
        background: isDark ? "#121212" : "#ffffff",
        ...theme.scrollbarStyles,
      },
      "& .MuiDialogActions-root": {
        flexShrink: 0,
        padding: "12px 16px",
        background: isDark ? "#121212" : "#ffffff",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
      "& .MuiButton-root": {
        borderRadius: 12,
      },
      "& .MuiSelect-icon": {
        right: 8,
      },
    },
    dialogHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 16px",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#ffffff",
      borderBottom: "none",
      flexShrink: 0,
    },
    dialogHeaderTitle: {
      fontWeight: 600,
      fontSize: 15,
      color: "inherit",
    },
    closeBtn: {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      color: "#ffffff",
      transition: "all 0.2s ease",
      padding: 6,
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.22)",
        color: "#ffffff",
      },
    },
    attachmentRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px dashed ${isDark ? "rgba(255,255,255,0.15)" : "rgba(15,23,42,0.15)"}`,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
    },
    attachmentName: {
      flex: 1,
      fontSize: 13,
      fontWeight: 500,
      color: isDark ? "#e9edef" : "#0f172a",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
    },
    deleteBtn: {
      color: "#e53935",
      padding: 6,
      transition: "all 0.2s ease",
      "&:hover": {
        color: "#c62828 !important",
        backgroundColor: "rgba(229,57,53,0.08) !important",
      },
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: isDark ? "#8696a0" : "#64748b",
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(0.5),
    },
  };
});

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required(i18n.t("validation.required")),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();
  const { user } = useContext(AuthContext);
  const messageInputRef = useRef();
  const attachmentFile = useRef(null);

  const initialState = {
    shortcode: "",
    message: "",
    geral: false,
    status: true,
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;
        const { data } = await api.get(`/quick-messages/${quickemessageId}`);
        setQuickemessage((prevState) => ({ ...prevState, ...data }));
      })();
    } catch (err) {
      toastError(err);
    }
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) setAttachment(file);
  };

  const handleSaveQuickeMessage = async (values) => {
    const quickemessageData = {
      ...values,
      isMedia: true,
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
        ? values.mediaPath.split("/").pop().replace(/ /g, "_")
        : null,
    };

    try {
      if (quickemessageId) {
        await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${quickemessageId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") reload();
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }
    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({ ...prev, mediaPath: null }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload === "function") reload();
    }
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;
    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);
    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  return (
    <>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.dialogHeader}>
          <Typography className={classes.dialogHeaderTitle}>
            {quickemessageId
              ? i18n.t("quickMessages.dialog.edit")
              : i18n.t("quickMessages.dialog.add")}
          </Typography>
          <IconButton size="small" onClick={handleClose} className={classes.closeBtn}>
            <CloseIcon size={16} />
          </IconButton>
        </div>

        <div style={{ display: "none" }}>
          <input type="file" ref={attachmentFile} onChange={handleAttachmentFile} />
        </div>

        <Formik
          initialValues={quickemessage}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => {
            const isLocked = quickemessageId && values.visao && !values.geral && values.userId !== user.id;
            return (
              <Form>
                <DialogContent>
                  <Grid spacing={2} container>
                    <Grid xs={12} item>
                      <Field
                        as={TextField}
                        autoFocus
                        label={i18n.t("quickMessages.dialog.shortcode")}
                        name="shortcode"
                        disabled={isLocked}
                        error={touched.shortcode && Boolean(errors.shortcode)}
                        helperText={touched.shortcode && errors.shortcode}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid xs={12} item>
                      <Field
                        as={TextField}
                        label={i18n.t("quickMessages.dialog.message")}
                        name="message"
                        inputRef={messageInputRef}
                        error={touched.message && Boolean(errors.message)}
                        helperText={touched.message && errors.message}
                        variant="outlined"
                        margin="dense"
                        disabled={isLocked}
                        multiline
                        minRows={6}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MessageVariablesPicker
                        disabled={isSubmitting || isLocked}
                        onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                      />
                    </Grid>

                    <Grid xs={12} item>
                      <Typography className={classes.sectionLabel}>
                        {i18n.t("quickMessages.dialog.visao")}
                      </Typography>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel id="visao-selection-label">
                          {i18n.t("quickMessages.dialog.visao")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("quickMessages.dialog.visao")}
                          labelId="visao-selection-label"
                          id="visao"
                          name="visao"
                          disabled={isLocked}
                          onChange={(e) => setFieldValue("visao", e.target.value === "true")}
                          error={touched.visao && Boolean(errors.visao)}
                          value={values.visao ? "true" : "false"}
                          MenuProps={{ classes: { paper: dropdownClasses.paper } }}
                        >
                          <MenuItem value="true">{i18n.t("quickMessages.dialog.allow")}</MenuItem>
                          <MenuItem value="false">{i18n.t("quickMessages.dialog.disallow")}</MenuItem>
                        </Field>
                      </FormControl>
                      {values.visao === true && (
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel id="geral-selection-label">
                            {i18n.t("quickMessages.dialog.geral")}
                          </InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("quickMessages.dialog.geral")}
                            labelId="geral-selection-label"
                            id="geral"
                            name="geral"
                            disabled={isLocked}
                            value={values.geral ? "true" : "false"}
                            error={touched.geral && Boolean(errors.geral)}
                            MenuProps={{ classes: { paper: dropdownClasses.paper } }}
                          >
                            <MenuItem value="true">{i18n.t("announcements.active")}</MenuItem>
                            <MenuItem value="false">{i18n.t("announcements.inactive")}</MenuItem>
                          </Field>
                        </FormControl>
                      )}
                    </Grid>

                    {(quickemessage.mediaPath || attachment) && (
                      <Grid xs={12} item>
                        <div className={classes.attachmentRow}>
                          <span className={classes.attachmentName}>
                            <Paperclip size={14} />
                            {attachment ? attachment.name : quickemessage.mediaName}
                          </span>
                          <AppTooltip titleKey="quickMessages.tooltips.removeAttachment">
                            <IconButton
                              onClick={() => setConfirmationOpen(true)}
                              disabled={isLocked}
                              className={classes.deleteBtn}
                              size="small"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </AppTooltip>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </DialogContent>
                <ChatiaDialogActions
                  type="submit"
                  disabled={isSubmitting || isLocked}
                  loading={isSubmitting}
                  saveText={
                    quickemessageId
                      ? i18n.t("quickMessages.buttons.edit")
                      : i18n.t("quickMessages.buttons.add")
                  }
                >
                  {!attachment && !quickemessage.mediaPath && (
                    <Button
                      onClick={() => attachmentFile.current.click()}
                      disabled={isSubmitting || isLocked}
                      variant="outlined"
                      startIcon={<Paperclip size={16} />}
                      style={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {i18n.t("quickMessages.buttons.attach")}
                    </Button>
                  )}
                </ChatiaDialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};

export default QuickMessageDialog;
